declare global {
  interface Window {
    onSpotifyWebPlaybackSDKReady: () => void;
    Spotify: any;
  }
}

export class SpotifyPlayer {
  private player: any = null;
  private deviceId: string | null = null;
  private token: string;
  private onReady: () => void;
  private onStateChange: (state: any) => void;
  private onError?: (error: any) => void;

  constructor(token: string, onReady: () => void, onStateChange: (state: any) => void, onError?: (error: any) => void) {
    this.token = token;
    this.onReady = onReady;
    this.onStateChange = onStateChange;
    this.onError = onError;
  }

  async init(): Promise<void> {
    return new Promise((resolve) => {
      // Load Spotify SDK
      if (!document.getElementById('spotify-sdk')) {
        const script = document.createElement('script');
        script.id = 'spotify-sdk';
        script.src = 'https://sdk.scdn.co/spotify-player.js';
        document.body.appendChild(script);
      }

      window.onSpotifyWebPlaybackSDKReady = () => {
        this.createPlayer();
        resolve();
      };
    });
  }

  private createPlayer(): void {
    this.player = new window.Spotify.Player({
      name: 'Audio Visualizer',
      getOAuthToken: (cb: (token: string) => void) => {
        cb(this.token);
      },
      volume: 0.5,
    });

    // Ready
    this.player.addListener('ready', ({ device_id }: { device_id: string }) => {
      console.log('Ready with Device ID', device_id);
      this.deviceId = device_id;
      this.onReady();
    });

    // Not Ready
    this.player.addListener('not_ready', ({ device_id }: { device_id: string }) => {
      console.log('Device ID has gone offline', device_id);
    });

    // Player state changed
    this.player.addListener('player_state_changed', (state: any) => {
      if (!state) return;
      this.onStateChange(state);
    });

    // Error handling
    this.player.addListener('initialization_error', ({ message }: { message: string }) => {
      console.error('Failed to initialize', message);
    });

    this.player.addListener('authentication_error', ({ message }: { message: string }) => {
      console.error('Failed to authenticate', message);
      if (this.onError) {
        this.onError({ message: '401 Unauthorized - ' + message });
      }
    });

    this.player.addListener('account_error', ({ message }: { message: string }) => {
      console.error('Failed to validate Spotify account', message);
    });

    this.player.addListener('playback_error', ({ message }: { message: string }) => {
      console.error('Failed to perform playback', message);
    });

    // Connect
    this.player.connect();
  }

  getDeviceId(): string | null {
    return this.deviceId;
  }

  async play(uri?: string): Promise<void> {
    if (!this.deviceId) return;

    const body: any = {
      device_id: this.deviceId,
    };

    if (uri) {
      if (uri.includes('track')) {
        body.uris = [uri];
      } else {
        body.context_uri = uri;
      }
    }

    const response = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${this.deviceId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      },
    });

    if (response.status === 401) {
      console.warn('🔒 Spotify play API returned 401 - Token expired');
      throw new Error('Unauthorized - Token expired');
    }
  }

  async pause(): Promise<void> {
    if (this.player) {
      await this.player.pause();
    }
  }

  async resume(): Promise<void> {
    if (this.player) {
      await this.player.resume();
    }
  }

  async togglePlay(): Promise<void> {
    if (this.player) {
      await this.player.togglePlay();
    }
  }

  async nextTrack(): Promise<void> {
    if (this.player) {
      await this.player.nextTrack();
    }
  }

  async previousTrack(): Promise<void> {
    if (this.player) {
      await this.player.previousTrack();
    }
  }

  async setVolume(volume: number): Promise<void> {
    if (this.player) {
      await this.player.setVolume(volume);
    }
  }

  async getCurrentState(): Promise<any> {
    if (this.player) {
      return await this.player.getCurrentState();
    }
    return null;
  }

  disconnect(): void {
    if (this.player) {
      this.player.disconnect();
    }
  }
}
